import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faKeyboard } from '@fortawesome/free-solid-svg-icons';
import './SurveyQuestions.css';

function SurveyQuestions({ 
    questions, 
    currentPage, 
    currentQuestion, 
    answerOptions, 
    answers, 
    lang, 
    onSelectOption, // Updates answers state for current question without navigating
    onPrev,
    onNext,
    isFirstPage 
}) {
    const { t } = useTranslation();
    const progressPercentage = ((currentPage + 1) / questions.length) * 100;
    
    // Check if the user has selected an answer for the current question
    const selectedAnswerValue = answers[currentQuestion?.order];
    const hasSelectedAnswer = selectedAnswerValue !== undefined && selectedAnswerValue !== null;

    return (
        <div className="survey-wrapper-bs min-vh-100 d-flex flex-column bg-canvas-slate">
            <div className="container-fluid flex-grow-1 d-flex align-items-center justify-content-center px-3 py-5">
                <div className="w-100" style={{ maxWidth: '42rem' }}>
                    <div className="card survey-card-bs border-0 shadow-sm p-4 p-md-5 rounded-4 bg-white">
                        <div className="d-flex flex-column w-100 gap-4">
                            
                            {/* Top Tracker & Progress Header */}
                            <div className="d-flex flex-column gap-2">
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge-category px-3 py-1 rounded-pill text-purple fw-semibold">
                                            {t('Clinical Self-Screen')}
                                        </span>
                                        <span className="text-muted">•</span>
                                        <span className="text-secondary small fw-medium">
                                            {t('Executive Function')}
                                        </span>
                                    </div>
                                    <div className="d-flex align-baseline gap-1">
                                        <span className="fs-5 fw-bold text-dark">{currentPage + 1}</span>
                                        <span className="text-muted small">/ {questions.length}</span>
                                    </div>
                                </div>

                                {/* Linear Gradient Progress Bar */}
                                <div className="progress rounded-pill bg-light" style={{ height: '8px' }}>
                                    <div
                                        className="progress-bar progress-bar-gradient rounded-pill transition-all"
                                        role="progressbar"
                                        style={{ width: `${progressPercentage}%` }}
                                        aria-valuenow={progressPercentage}
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                    ></div>
                                </div>
                            </div>

                            {/* Question Core Display */}
                            <div className="d-flex flex-column gap-2 pt-1">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="pulse-dot rounded-circle bg-purple"></span>
                                    <span className="text-uppercase text-purple fw-bold small tracking-wider">
                                        Question {String(currentPage + 1).padStart(2, '0')}
                                    </span>
                                </div>
                                <h1 className="survey-question-title fw-bold text-dark mb-1">
                                    {currentQuestion?.translations[lang] || currentQuestion?.translations['en']}
                                </h1>
                            </div>

                            {/* Answer Stack */}
                            <div className="d-flex flex-column gap-3 pt-1">
                                {answerOptions.map((option, index) => {
                                    const isSelected = selectedAnswerValue === option.value;
                                    return (
                                        <div
                                            key={option.value}
                                            onClick={() => onSelectOption(currentQuestion.order, option.value)}
                                            className={`survey-option-card d-flex align-items-center justify-content-between p-3 rounded-4 cursor-pointer ${
                                                isSelected ? 'selected' : 'default'
                                            }`}
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <span className={`option-number-badge d-flex align-items-center justify-content-center rounded-circle fw-semibold small ${
                                                    isSelected ? 'bg-purple text-white shadow-sm' : 'bg-light text-secondary'
                                                }`}>
                                                    {index + 1}
                                                </span>
                                                <span className={`option-label-text fw-medium ${
                                                    isSelected ? 'text-purple-deep fw-semibold' : 'text-dark'
                                                }`}>
                                                    {option.translations[lang] || option.translations['en']}
                                                </span>
                                            </div>
                                            <div className={`radio-indicator rounded-circle d-flex align-items-center justify-content-center ${
                                                isSelected ? 'bg-purple shadow-sm' : 'bg-light'
                                            }`}>
                                                <div className={`radio-dot rounded-circle bg-white ${
                                                    isSelected ? 'opacity-100' : 'opacity-0'
                                                }`}></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Bottom Controls */}
                            <div className="d-flex flex-column gap-3 pt-2">
                                <div className="d-flex align-items-center justify-content-between gap-3">
                                    <button
                                        type="button"
                                        onClick={onPrev}
                                        disabled={isFirstPage}
                                        className={`btn btn-light px-4 py-2 rounded-4 fw-semibold d-inline-flex align-items-center gap-2 text-purple shadow-sm ${
                                            isFirstPage ? 'opacity-50 pointer-events-none' : ''
                                        }`}
                                    >
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                        <span>{t('Previous')}</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={onNext}
                                        disabled={!hasSelectedAnswer}
                                        className={`btn btn-purple-gradient px-4 py-2 rounded-4 fw-semibold text-white d-inline-flex align-items-center gap-2 shadow ${
                                            !hasSelectedAnswer ? 'opacity-50 pointer-events-none' : ''
                                        }`}
                                    >
                                        <span>{t('Next Question')}</span>
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SurveyQuestions;